import { jsPDF } from 'jspdf';

interface DailyCutData {
    sales: any[];
    payments: any[];
    date: string; // ISO string or Date object
    user?: string; // Who generated the report
}

// Helper to draw or calculate height
const drawDailyCutContent = (doc: any | null, data: DailyCutData): number => {
    let yPos = 5;

    // If doc is null, we are just calculating height
    const isCalc = !doc;

    const addText = (text: string, x: number, y: number, options?: any) => {
        if (!isCalc) doc.text(text, x, y, options);
    };

    const setFont = (font: string, style: string) => {
        if (!isCalc) doc.setFont(font, style);
    };

    const setFontSize = (size: number) => {
        if (!isCalc) doc.setFontSize(size);
    };

    const setTextColor = (r: number, g: number, b: number) => {
        if (!isCalc) doc.setTextColor(r, g, b);
    };

    const setLineWidth = (width: number) => {
        if (!isCalc) doc.setLineWidth(width);
    };

    const line = (x1: number, y1: number, x2: number, y2: number) => {
        if (!isCalc) doc.line(x1, y1, x2, y2);
    };

    const pageWidth = 80;
    const margin = 5;

    // --- HEADER ---
    setTextColor(0, 0, 0);
    setFontSize(14);
    setFont('helvetica', 'bold');
    addText('BOUTIQUE MARIA REGNA', pageWidth / 2, yPos, { align: 'center' });

    yPos += 5;
    setFontSize(10);
    setFont('helvetica', 'normal');
    addText('CORTE DE CAJA DEL DÍA', pageWidth / 2, yPos, { align: 'center' });

    yPos += 5;
    setFontSize(9);
    const reportDate = new Date(data.date);
    // Manual formatting for CDMX roughly, or just use provided date string if pre-formatted
    // Assuming input is ISO, we format it.
    const dateStr = reportDate.toLocaleDateString('es-MX', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
    const timeStr = reportDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

    addText(`${dateStr}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
    addText(`Generado: ${timeStr}`, pageWidth / 2, yPos, { align: 'center' });

    yPos += 5;
    setLineWidth(0.3);
    line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;

    // --- FINANCIAL SUMMARY ---
    setFontSize(11);
    setFont('helvetica', 'bold');
    addText('RESUMEN FINANCIERO', margin, yPos);
    yPos += 5;

    // Calculate Totals from PAYMENTS (Dynamic Grouping)
    const methodTotals: Record<string, number> = {};
    let totalIngresos = 0;

    data.payments.forEach(p => {
        const amount = Number(p.monto || p.amount || 0);
        const rawMethod = (p.metodo || p.method || 'OTROS').trim();
        const methodUpper = rawMethod.toUpperCase();
        
        let normalizedMethod = rawMethod;

        // Consolidation Logic
        if (methodUpper.includes('EFECTIVO') || methodUpper.includes('CASH')) normalizedMethod = 'Efectivo';
        else if (methodUpper.includes('TARJETA') || methodUpper.includes('CARD')) normalizedMethod = 'Tarjeta';
        else if (methodUpper.includes('TRANSFER') || methodUpper.includes('DEPOSITO')) normalizedMethod = 'Transferencia';
        else {
            // Normalize specific "other" methods to Title Case for cleaner UI
            normalizedMethod = rawMethod.charAt(0).toUpperCase() + rawMethod.slice(1).toLowerCase();
        }

        methodTotals[normalizedMethod] = (methodTotals[normalizedMethod] || 0) + amount;
        totalIngresos += amount;
    });

    // Render Summary Table
    setFontSize(9);
    setFont('helvetica', 'normal');

    const addSummaryRow = (label: string, amount: number) => {
        addText(`${label}:`, margin, yPos);
        addText(`$${amount.toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });
        yPos += 4;
    };

    // Sort priority: Efectivo, Tarjeta, Transferencia, then Alphabetical Others
    const sortedMethods = Object.keys(methodTotals).sort((a, b) => {
        const priority: Record<string, number> = { 'Efectivo': 1, 'Tarjeta': 2, 'Transferencia': 3 };
        const pA = priority[a] || 99;
        const pB = priority[b] || 99;
        if (pA !== pB) return pA - pB;
        return a.localeCompare(b);
    });

    sortedMethods.forEach(method => {
        addSummaryRow(method, methodTotals[method]);
    });

    yPos += 1;
    setFont('helvetica', 'bold');
    line(margin, yPos, pageWidth - margin, yPos);
    yPos += 4;
    addSummaryRow('TOTAL INGRESOS:', totalIngresos);

    yPos += 6;

    // Stats from SALES (Operational)
    const totalSalesCount = data.sales.length;
    // Filter layaways/apartados based on status or logic
    const apartadosCount = data.sales.filter(s => s.estado === 'APARTADO' || s.estado === 'PENDIENTE').length;

    setFontSize(9);
    setFont('helvetica', 'bold');
    addText('ESTADÍSTICAS OPERATIVAS', margin, yPos);
    yPos += 4;
    setFont('helvetica', 'normal');
    addText(`Total Transacciones: ${totalSalesCount}`, margin, yPos);
    yPos += 4;
    addText(`Apartados Nuevos: ${apartadosCount}`, margin, yPos);
    yPos += 4;

    yPos += 4;
    setLineWidth(0.3);
    line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;

    // --- DETAILED LIST ---
    setFontSize(11);
    setFont('helvetica', 'bold');
    addText('DETALLE DE MOVIMIENTOS', margin, yPos);
    yPos += 5;

    // We can list Payments or Sales. User asked for "Lista detallada de cada venta... con: Folio, Vendedora, Productos... Metodos de pago".
    // The 'data.sales' from getReportSales has this info.
    // However, `data.payments` has the actual time of payment.
    // Using Sales list is better for Product details.

    data.payments.forEach((payment) => {
        // Top Line: Folio | Time | Amount (of THIS payment)
        setFontSize(9);
        setFont('helvetica', 'bold');

        const paymentDate = new Date(payment.fecha);
        const time = paymentDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

        addText(`${payment.folioVenta || 'N/A'} - ${time}`, margin, yPos);
        addText(`$${Number(payment.monto).toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });
        yPos += 4;

        // Second Line: Cajero | Cliente
        setFontSize(8);
        setFont('helvetica', 'normal');
        const vendedora = (payment.cajero || 'N/A').split(' ')[0];
        addText(`${vendedora} | ${payment.cliente || 'Publico'}`, margin, yPos);
        yPos += 3.5;

        // Identify if it's a new sale or an abono/liquidacion
        const isNewSale = payment.tipo === 'Pago Directo' || payment.tipo === 'Primer Abono';
        
        if (isNewSale) {
            // New Sale: Show items if available in sales data
            const assocSale = data.sales.find(s => s.folio === payment.folioVenta);
            if (assocSale && assocSale.items) {
                setFontSize(7);
                setFont('helvetica', 'italic');
                const itemsStr = assocSale.items;
                const maxChars = 55;
                if (itemsStr.length > maxChars) {
                    addText(itemsStr.substring(0, maxChars), margin, yPos);
                    yPos += 3;
                    addText(itemsStr.substring(maxChars), margin, yPos);
                } else {
                    addText(itemsStr, margin, yPos);
                }
                yPos += 3.5;
            }
        } else {
            // Abono / Liquidacion: Show the specific type from backend
            setFontSize(7);
            setFont('helvetica', 'bold');
            setTextColor(100, 100, 100);
            addText(`*** ${payment.tipo?.toUpperCase() || 'PAGO DE VENTA ANTERIOR'} ***`, margin, yPos);
            setTextColor(0, 0, 0);
            yPos += 3.5;
        }

        // Final Line: Method
        setFontSize(8);
        setFont('helvetica', 'normal');
        addText(`Mtd: ${payment.metodo}`, margin, yPos);
        yPos += 2;

        // Separator
        yPos += 2;
        setLineWidth(0.1);
        line(margin, yPos, pageWidth - margin, yPos);
        yPos += 3;
    });

    // Footer
    yPos += 5;
    setFontSize(10);
    setFont('helvetica', 'bold');
    addText('--- FIN DEL REPORTE ---', pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;

    return yPos;
};

export const generateDailyCutPDF = (sales: any[], payments: any[], reportDate?: string | Date): void => {
    const dateToUse = reportDate ? new Date(reportDate) : new Date();

    const data: DailyCutData = {
        sales,
        payments,
        date: dateToUse.toISOString(),
    };

    console.log('Generando Corte de Caja PDF', data);

    // 1. Calculate Height (Dry Run)
    const requiredHeight = drawDailyCutContent(null, data);

    // 2. Create PDF
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [80, requiredHeight + 10], // Add a little padding buffer
    });

    // 3. Render
    drawDailyCutContent(doc, data);

    // 4. Save
    const dateStr = dateToUse.toISOString().split('T')[0];
    doc.save(`corte_caja_${dateStr}.pdf`);
};
